import { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext.jsx';
import POSHeader from './POSSection/POSHeader';

function InventoryCheckout() {
  const [inventoryItems, setInventoryItems] = useState([]);
  const [staff, setStaff] = useState([]);
  const [cart, setCart] = useState([]);
  const [search, setSearch] = useState('');
  const [barcode, setBarcode] = useState('');
  const [selectedStaff, setSelectedStaff] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mobileView, setMobileView] = useState('items'); // 'items' | 'cart'
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  // ✅ KIOSK TOKEN HELPER: Get kiosk-specific auth token
  const getAuthToken = () => {
    return localStorage.getItem('kioskAuthToken') || localStorage.getItem('token');
  };

  // Filter for active staff only
  const activeStaff = useMemo(() =>
    staff.filter(s => s.status === 'active') || [],
    [staff]
  );

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = getAuthToken();
        if (!token) {
          throw new Error('No token found. Please log in.');
        }
        
        const [inventoryRes, staffRes] = await Promise.all([
          axios.get(`${import.meta.env.VITE_API_URL}/api/inventory`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 10000
          }),
          axios.get(`${import.meta.env.VITE_API_URL}/api/staff`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 10000
          }),
        ]);
        
        console.log('Inventory Response:', inventoryRes.data);
        console.log('Staff Response:', staffRes.data);
        
        const inventoryData = Array.isArray(inventoryRes.data) ? inventoryRes.data : [];
        const sortedItems = inventoryData.sort((a, b) => {
          const order = ['Salon', 'Gym', 'Spa'];
          return order.indexOf(a.subBusiness) - order.indexOf(b.subBusiness);
        });
        
        setInventoryItems(sortedItems);
        setStaff(Array.isArray(staffRes.data) ? staffRes.data : []);
        setError('');
        setLoading(false);
        
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(`Failed to load data: ${err.message}. Please check your internet connection.`);
        setLoading(false);
        if (err.response?.status === 401) {
          logout();
          navigate('/kiosk/login', { state: { message: 'Session expired. Please log in again.' } });
        }
      }
    };
    fetchData();
  }, [navigate]);

  const addToCart = (item) => {
    if (!item._id || !item.name || item.quantity == null) {
      alert('Invalid item data. Please try again.');
      return;
    }
    const existingItem = cart.find((i) => i._id === item._id);
    if (existingItem) {
      setCart(cart.map((i) => (i._id === item._id ? { ...i, cartQty: i.cartQty + 1 } : i)));
    } else {
      setCart([...cart, { ...item, cartQty: 1 }]);
    }
  };

  const handleScan = async () => {
    if (!barcode) return;
    try {
      const token = getAuthToken();
      if (!token) throw new Error('No token found. Please log in.');
      const res = await axios.get(`${import.meta.env.VITE_API_URL}/api/inventory?sku=${barcode}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('Barcode Scan Response:', res.data);
      if (Array.isArray(res.data) && res.data.length > 0) {
        addToCart(res.data[0]);
        setBarcode('');
      } else {
        alert('Item not found');
      }
    } catch (err) {
      console.error('Error scanning barcode:', err);
      alert('Error scanning barcode: ' + (err.message || 'Unknown error'));
    }
  };

  const removeFromCart = (id) => {
    setCart(cart.filter((item) => item._id !== id));
  };

  const updateCartQty = (id, qty) => {
    setCart(
      cart.map((item) =>
        item._id === id ? { ...item, cartQty: Math.max(1, parseInt(qty) || 1) } : item
      )
    );
  };

  const handleRequestItem = async () => {
    if (cart.length === 0 || !selectedStaff || !reason) {
      alert('Please add items, select a staff member, and provide a reason');
      return;
    }
    setLoading(true);

    try {
      const token = getAuthToken();
      if (!token) throw new Error('No token found. Please log in.');
      
      const staffMember = staff.find((s) => s._id === selectedStaff);
      if (!staffMember) {
        throw new Error('Selected staff member not found');
      }

      const updates = cart.map((item) => {
        if (!item._id || item.cartQty <= 0) {
          throw new Error(`Invalid cart item: ${item.name || 'Unknown'} (ID: ${item._id}, Quantity: ${item.cartQty})`);
        }
        return { itemId: item._id, quantity: item.cartQty };
      });

      const requestData = {
        updates,
        staffId: selectedStaff,
        reason,
        subBusiness: cart[0]?.subBusiness || 'General',
      };

      console.log('Sending inventory request:', requestData);

      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/api/inventory-checkouts`, 
        requestData, 
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000
        }
      );

      // Update local inventory
      setInventoryItems((prevItems) =>
        prevItems.map((i) => {
          const cartItem = cart.find((c) => c._id === i._id);
          if (cartItem) {
            return { ...i, quantity: i.quantity - cartItem.cartQty };
          }
          return i;
        })
      );

      const receiptText = `Inventory Request Receipt\nItems:\n${cart.map((i) => `${i.name} - Qty: ${i.cartQty}`).join('\n')}\nStaff: ${staffMember.firstName} ${staffMember.lastName}\nReason: ${reason}\nReceipt ID: ${response.data.receiptId}`;
      alert(receiptText);
      alert('Inventory request completed successfully');

      // Clear form
      setCart([]);
      setSelectedStaff('');
      setReason('');
      setBarcode('');
      setError('');
      
    } catch (err) {
      console.error('Error requesting items:', {
        error: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });
      setError('Error requesting items: ' + (err.response?.data?.message || err.message));
      alert('Error requesting items: ' + (err.response?.data?.message || err.message) + '. Please check your internet connection.');
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = inventoryItems.filter(
    (item) =>
      item.name?.toLowerCase().includes(search.toLowerCase()) ||
      item.sku?.toLowerCase().includes(search.toLowerCase())
  );

  const groupedItems = filteredItems.reduce((acc, item) => {
    if (!acc[item.subBusiness]) {
      acc[item.subBusiness] = [];
    }
    acc[item.subBusiness].push(item);
    return acc;
  }, {});

  return (
    <div
      className="flex flex-col md:flex-row min-h-screen w-full"
      style={{
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '14px',
        background: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)'
      }}
    >
      {/* Main Content Area */}
      <div className={`w-full md:w-[70%] p-3 md:p-6 md:overflow-y-auto md:max-h-screen ${mobileView === 'cart' ? 'hidden md:block' : 'block'}`}>
        <POSHeader user={user} onLogout={logout} />

        {/* Title */}
        <div className="text-center mb-4 md:mb-8">
          <h2
            className="text-xl md:text-3xl font-bold mb-2"
            style={{ color: '#ffffff', fontFamily: 'Inter, sans-serif', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}
          >
            Inventory Checkout
          </h2>
          <div className="w-32 h-1 mx-auto rounded-full" style={{ background: 'linear-gradient(90deg, #10b981, #34d399)' }}></div>
        </div>

        {/* Search and Barcode Section */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4 md:mb-6">
          <div className="flex-1">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search items by name or SKU..."
              className="w-full p-3 md:p-4 rounded-xl border-2 shadow-lg"
              style={{
                backgroundColor: 'rgba(255,255,255,0.95)',
                color: '#1f2937',
                fontFamily: 'Inter, sans-serif',
                fontSize: '14px',
                borderColor: 'rgba(16,185,129,0.3)',
                boxSizing: 'border-box'
              }}
            />
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleScan()}
              placeholder="Scan barcode..."
              className="flex-1 sm:w-40 p-3 md:p-4 rounded-xl border-2 shadow-lg"
              style={{
                backgroundColor: 'rgba(255,255,255,0.95)',
                color: '#1f2937',
                fontFamily: 'Inter, sans-serif',
                fontSize: '14px',
                borderColor: 'rgba(16,185,129,0.3)',
                boxSizing: 'border-box'
              }}
            />
            <button
              onClick={handleScan}
              className="px-4 md:px-6 py-3 md:py-4 rounded-xl font-medium shadow-lg"
              style={{ background: 'linear-gradient(135deg, #10b981, #059669)', color: '#ffffff', fontFamily: 'Inter, sans-serif', fontSize: '14px', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              Scan
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div 
            className="p-4 rounded-xl mb-6 border-l-4"
            style={{ 
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              borderColor: '#ef4444',
              color: '#fca5a5'
            }}
          >
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div 
              className="animate-spin rounded-full h-16 w-16 border-4"
              style={{ 
                borderColor: 'rgba(16, 185, 129, 0.3)',
                borderTopColor: '#10b981'
              }}
            ></div>
          </div>
        ) : (
          /* Inventory Items */
          <div className="space-y-8">
            {Object.entries(groupedItems).map(([subBusiness, subItems]) => (
              <div key={subBusiness}>
                <h3 
                  className="text-xl font-semibold mb-4 flex items-center"
                  style={{ color: '#ffffff', fontFamily: 'Inter, sans-serif' }}
                >
                  <div 
                    className="w-3 h-3 rounded-full mr-3"
                    style={{ 
                      backgroundColor: subBusiness === 'Salon' ? '#10b981' : 
                                     subBusiness === 'Gym' ? '#3b82f6' : '#8b5cf6'
                    }}
                  ></div>
                  {subBusiness}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {subItems.map((item) => (
                    <div
                      key={item._id}
                      onClick={() => addToCart(item)}
                      className="p-4 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer hover:scale-105 border"
                      style={{
                        background: subBusiness === 'Salon' 
                          ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.9), rgba(5, 150, 105, 0.9))'
                          : subBusiness === 'Gym' 
                          ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.9), rgba(37, 99, 235, 0.9))'
                          : 'linear-gradient(135deg, rgba(139, 92, 246, 0.9), rgba(124, 58, 237, 0.9))',
                        color: '#ffffff',
                        fontFamily: 'Inter, sans-serif',
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                        backdropFilter: 'blur(10px)'
                      }}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <h4 
                          className="font-semibold text-sm leading-tight"
                          style={{ color: '#022835ff' }}
                        >
                          {item.name}
                        </h4>
                        <div 
                          className="px-2 py-1 rounded-full text-xs font-medium"
                          style={{ 
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            backdropFilter: 'blur(5px)'
                          }}
                        >
                          {item.quantity}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs opacity-90">
                          SKU: {item.sku || 'N/A'}
                        </p>
                        <div 
                          className="inline-block px-2 py-1 rounded text-xs font-medium"
                          style={{ 
                            backgroundColor: 'rgba(255, 255, 255, 0.15)',
                            backdropFilter: 'blur(5px)'
                          }}
                        >
                          {item.subBusiness}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Mobile: sticky "View Cart" button */}
        {cart.length > 0 && (
          <div
            className="md:hidden sticky bottom-0 mt-4 pt-2 pb-4"
            style={{ background: 'linear-gradient(to top, rgba(15,32,39,1) 60%, transparent)' }}
          >
            <button
              onClick={() => setMobileView('cart')}
              style={{
                width: '100%',
                padding: '1rem 1.25rem',
                border: 'none',
                borderRadius: '0.875rem',
                background: 'rgba(255,255,255,0.96)',
                color: '#065f46',
                fontSize: '1rem',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontFamily: 'Inter, sans-serif',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
              }}
            >
              <span>View Request Cart ({cart.length} item{cart.length !== 1 ? 's' : ''})</span>
              <span style={{ color: '#10b981', fontWeight: '800' }}>→</span>
            </button>
          </div>
        )}
      </div>

      {/* Cart Sidebar — fixed full-screen on mobile, sidebar on desktop */}
      <div
        className={`md:w-[30%] md:static md:flex md:flex-col ${
          mobileView === 'items' ? 'hidden md:flex' : 'fixed inset-0 z-[80] flex flex-col'
        }`}
        style={{
          background: 'rgba(255,255,255,0.97)',
          backdropFilter: 'blur(20px)'
        }}
      >
        {/* Mobile back button */}
        <button
          onClick={() => setMobileView('items')}
          className="md:hidden flex items-center gap-2 w-full px-4 py-3 text-sm font-semibold text-emerald-700 bg-emerald-50"
          style={{ border: 'none', borderBottom: '1px solid #d1fae5', cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}
        >
          ← Back to Items
        </button>

      <div
        className="p-4 md:p-6 shadow-2xl flex flex-col flex-1 border-l overflow-y-auto"
        style={{
          borderColor: 'rgba(16, 185, 129, 0.2)'
        }}
      >
        <div className="flex items-center mb-6">
          <div 
            className="w-4 h-4 rounded-full mr-3"
            style={{ backgroundColor: '#10b981' }}
          ></div>
          <h3 
            className="text-xl font-bold"
            style={{ 
              color: '#1f2937', 
              fontFamily: 'Inter, sans-serif'
            }}
          >
            Request Cart
          </h3>
          {cart.length > 0 && (
            <div 
              className="ml-auto px-3 py-1 rounded-full text-sm font-medium"
              style={{ 
                backgroundColor: '#10b981',
                color: '#ffffff'
              }}
            >
              {cart.length}
            </div>
          )}
        </div>

        {cart.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div 
                className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)' }}
              >
                <div 
                  className="w-8 h-8 rounded-full"
                  style={{ backgroundColor: 'rgba(16, 185, 129, 0.3)' }}
                ></div>
              </div>
              <p 
                className="text-lg font-medium"
                style={{ color: '#6b7280', fontFamily: 'Inter, sans-serif' }}
              >
                No items selected
              </p>
              <p 
                className="text-sm mt-1"
                style={{ color: '#9ca3af', fontFamily: 'Inter, sans-serif' }}
              >
                Click on items to add them to your request
              </p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col h-full">
            {/* Cart Items */}
            <div className="mb-6 flex-grow overflow-y-auto" style={{ maxHeight: 'calc(100vh - 400px)' }}>
              <div className="space-y-3">
                {cart.map((item) => (
                  <div 
                    key={item._id} 
                    className="p-4 rounded-xl border shadow-sm"
                    style={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      borderColor: 'rgba(16, 185, 129, 0.2)'
                    }}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h4 
                          className="font-semibold text-sm mb-1"
                          style={{ color: '#1f2937', fontFamily: 'Inter, sans-serif' }}
                        >
                          {item.name}
                        </h4>
                        <div 
                          className="inline-block px-2 py-1 rounded text-xs font-medium"
                          style={{ 
                            backgroundColor: item.subBusiness === 'Salon' ? 'rgba(16, 185, 129, 0.1)'
                                           : item.subBusiness === 'Gym' ? 'rgba(59, 130, 246, 0.1)'
                                           : 'rgba(139, 92, 246, 0.1)',
                            color: item.subBusiness === 'Salon' ? '#059669'
                                 : item.subBusiness === 'Gym' ? '#2563eb'
                                 : '#7c3aed'
                          }}
                        >
                          {item.subBusiness}
                        </div>
                      </div>
                      <button
                        onClick={() => removeFromCart(item._id)}
                        className="p-2 rounded-lg transition-all duration-300 hover:scale-105"
                        style={{ 
                          backgroundColor: 'rgba(239, 68, 68, 0.1)',
                          color: '#ef4444'
                        }}
                      >
                        ×
                      </button>
                    </div>
                    <div className="flex items-center justify-between">
                      <span 
                        className="text-sm font-medium"
                        style={{ color: '#6b7280' }}
                      >
                        Quantity
                      </span>
                      <input
                        type="number"
                        value={item.cartQty}
                        onChange={(e) => updateCartQty(item._id, parseInt(e.target.value))}
                        className="w-20 p-2 rounded-lg border text-center font-medium"
                        min="1"
                        max={item.quantity}
                        style={{ 
                          backgroundColor: '#ffffff',
                          color: '#1f2937',
                          borderColor: 'rgba(16, 185, 129, 0.3)',
                          fontFamily: 'Inter, sans-serif'
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Form Fields */}
            <div className="space-y-4">
              <div>
                <label 
                  className="block text-sm font-semibold mb-2"
                  style={{ color: '#374151', fontFamily: 'Inter, sans-serif' }}
                >
                  Requested By
                </label>
                <select
                  value={selectedStaff}
                  onChange={(e) => setSelectedStaff(e.target.value)}
                  className="w-full p-3 rounded-xl border-2 transition-all duration-300"
                  style={{ 
                    backgroundColor: '#ffffff',
                    color: '#1f2937',
                    fontFamily: 'Inter, sans-serif',
                    borderColor: 'rgba(16, 185, 129, 0.3)'
                  }}
                >
                  <option value="">Select Staff Member</option>
                  {activeStaff.map((s) => (
                    <option key={s._id} value={s._id}>
                      {s.firstName} {s.lastName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label 
                  className="block text-sm font-semibold mb-2"
                  style={{ color: '#374151', fontFamily: 'Inter, sans-serif' }}
                >
                  Reason for Request
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full p-3 rounded-xl border-2 transition-all duration-300 resize-none"
                  rows="3"
                  placeholder="Enter the reason for this inventory request..."
                  style={{ 
                    backgroundColor: '#ffffff',
                    color: '#1f2937',
                    fontFamily: 'Inter, sans-serif',
                    borderColor: 'rgba(16, 185, 129, 0.3)'
                  }}
                />
              </div>

              <button
                onClick={handleRequestItem}
                disabled={loading}
                className="w-full p-4 rounded-xl font-bold transition-all duration-300 hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ 
                  background: loading ? '#9ca3af' : 'linear-gradient(135deg, #10b981, #059669)',
                  color: '#ffffff',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '16px',
                  border: 'none'
                }}
              >
                {loading ? 'Processing Request...' : 'Submit Request'}
              </button>
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

export default InventoryCheckout;